import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconId,
  IconLock,
  IconInfoCircle,
  IconEye,
  IconEyeOff,
  IconCheck,
} from '@tabler/icons-react';
import { Checkbox } from '@mantine/core';
import { useEffect } from 'react';
import { Button } from '../../components/common/Button';
import { Card } from '../../components/common/Card';
import { Input } from '../../components/common/Input';
import apiService from '../../services/ApiService';
import { getPublicFolderAssetUrl } from '../../services/authNavigation';
import { toast } from '../../components/common/Toaster';
import { useCameraContext } from '../../contexts/CameraContext';
import { CameraPreview } from '../dashboard/components/CameraPreview';
import classes from './css/LoginPage.module.css';

function safeReturnPath(from: unknown): string | null {
  if (typeof from !== 'string' || !from.startsWith('/') || from.startsWith('//')) return null;
  return from;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cameraConsent, setCameraConsent] = useState(false);

  const {
    stream,
    error: cameraError,
    isActive: isCameraActive,
    startCamera,
    stopCamera,
  } = useCameraContext();

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, [startCamera, stopCamera]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!identifier || !password || submitting) return;
    try {
      setSubmitting(true);
      await apiService.login(identifier, password);
      const from = safeReturnPath((location.state as { from?: string } | null)?.from);
      navigate(from ?? '/dashboard', { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 401 && data?.code === 503 && data?.msg === 'No Data Available.') {
        toast.error('Username/password incorrect');
      } else {
        const msg =
          data?.message ||
          data?.msg ||
          err?.message ||
          'Login failed. Please check your credentials.';
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={classes.page}>
      <main className={classes.main}>
        <Card className={classes.card} shadow="md" radius="md">
          <div className={classes.logoWrap}>
            <img
              src={getPublicFolderAssetUrl('75-Years-Logo.png')}
              alt="75 Years Amrit Mahotsav"
              className={classes.logoImg}
            />
          </div>

          <h1 className={classes.title}>Student Login</h1>
          <p className={classes.subtitle}>
            Enable your camera and enter your credentials to access the portal
          </p>

          <div className={classes.cameraPreCheck}>
            <CameraPreview
              stream={stream}
              error={cameraError}
              isActive={isCameraActive}
              className={classes.loginCamera}
            />
          </div>

          <form className={classes.form} onSubmit={handleSubmit}>
            <Input
              type="text"
              label="LoginId"
              placeholder="Enter your LoginId"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              name="identifier"
              required
              className={classes.input}
              leftSection={<IconId size={18} />}
              leftSectionPointerEvents="none"
            />
            <Input
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              name="password"
              required
              className={classes.input}
              leftSection={<IconLock size={18} />}
              leftSectionPointerEvents="none"
              rightSection={
                <button
                  type="button"
                  className={classes.passwordToggle}
                  onClick={() => setShowPassword((p) => !p)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              }
            />

            <div className={classes.infoBox} role="alert">
              <IconInfoCircle size={22} className={classes.infoIcon} aria-hidden />
              <p className={classes.infoText}>
                Your password was sent to you via email and sms along with your{' '}
                <strong>Hall Ticket</strong>. Please check your inbox or spam folder.
              </p>
            </div>

            <Checkbox
              label="I consent to the use of camera for proctoring during the exam."
              checked={cameraConsent}
              onChange={(e) => setCameraConsent(e.currentTarget.checked)}
              className={classes.consentCheckbox}
              color="grape"
              radius="sm"
              size="sm"
            />

            <Button
              type="submit"
              className={classes.loginBtn}
              disabled={submitting || !isCameraActive || !cameraConsent}
            >
              {submitting ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
